import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { formatDate, getRoleLabel } from '../lib/utils';
import { COUNTRIES, NATIONALITIES } from '../lib/countries';
import api from '../lib/api';
import { turkishIncludes } from '../lib/turkishSearch';
import { getPreviewUrl, downloadDocument } from '../lib/documentPreview';
import { validateRRN, getRRNErrorMessage, getRRNWarningMessage, stripRRN, formatRRN, validateNationalId, formatNationalId, getPlaceholder, getMaxDigits, stripDigits } from '../utils/rijksregisternummer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { 
  User, 
  FileText, 
  Calendar, 
  Euro, 
  Save, 
  Loader2, 
  Upload,
  Download,
  Trash2,
  Eye,
  EyeOff,
  Camera,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Building2,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Heart,
  IdCard,
  Car,
  FileCheck2,
  Users,
  Briefcase,
  Clock,
  TrendingUp,
  TrendingDown,
  FileCheck,
  X,
  Wallet,
  Zap,
  Info,
  Package,
  BarChart3,
  Filter,
  Lock,
  Shield,
  Globe,
  Settings,
  Sparkles,
  Check,
  StickyNote,
  Search,
  Archive,
  ArchiveRestore
} from 'lucide-react';
import { Slider } from '../components/ui/slider';
import ImageCropper from '../components/ImageCropper';
import DocumentImageEditor from '../components/DocumentImageEditor';
import { SecuritySettings } from '../components/SecuritySettings';
import { UserVehicleHistory } from '../components/UserVehicleHistory';
import { UserAssignmentHistory } from '../components/UserAssignmentHistory';
import Tesseract from 'tesseract.js';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// Chart colors
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];

// Important document types that should be highlighted
const IMPORTANT_DOC_TYPES = ['identiteitskaart', 'paspoort', 'rijbewijs'];

// Document type keys for i18n mapping
const DOCUMENT_TYPE_KEYS = {
  identiteitskaart: 'idCard',
  paspoort: 'passport',
  rijbewijs: 'driverLicense',
  verblijfsvergunning: 'residencePermit',
  arbeidsovereenkomst: 'workContract',
  diploma: 'diploma',
  medisch_attest: 'medicalCert',
  bankgegevens: 'bankDetails',
  belastingfiche: 'taxForm',
  andere: 'other'
};

// Finance type keys for i18n mapping
const FINANCE_TYPE_KEYS = {
  avans: { key: 'advance', color: 'bg-yellow-500' },
  pot_plus: { key: 'potPlus', color: 'bg-emerald-500' },
  pot_minus: { key: 'potMinus', color: 'bg-rose-500' },
  laadvergoeding: { key: 'laadvergoeding', color: 'bg-cyan-500' }
};

// Marital status keys for i18n mapping
const MARITAL_STATUS_KEYS = {
  ongehuwd: 'single',
  gehuwd: 'married',
  gescheiden: 'divorced',
  weduwe_weduwnaar: 'widowed',
  wettelijk_samenwonend: 'cohabiting'
};

export default function EmployeeProfile() {
  const { t } = useTranslation();
  const { language, setLanguage } = useI18nStore();
  const { user, updateLanguage } = useAuthStore();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('personal');
  const [myNotes, setMyNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSearch, setNotesSearch] = useState('');
  const [notesDateFilter, setNotesDateFilter] = useState('');
  const [notesShowArchived, setNotesShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [idleTimeout, setIdleTimeout] = useState(user?.idle_timeout_minutes || 5);
  const [idleTimeoutSaving, setIdleTimeoutSaving] = useState(false);
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: ''
  });
  
  // Profile data
  const [profile, setProfile] = useState(null);
  const [profileExists, setProfileExists] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [metadata, setMetadata] = useState(null);
  
  // Profile Photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoCropperOpen, setPhotoCropperOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // LinkedIn-style title/bio were removed per user feedback. Keeping the
  // profile-photo change button on the compact header instead.
  const [firmName, setFirmName] = useState(null);
  const [myPostnl, setMyPostnl] = useState([]);
  
  // Documents
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [docForm, setDocForm] = useState({
    document_type: '',
    document_number: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });
  
  // Important document upload
  const [importantDocType, setImportantDocType] = useState(null);
  const [importantDocFile, setImportantDocFile] = useState(null);
  const [uploadingImportantDoc, setUploadingImportantDoc] = useState(false);
  const importantDocInputRef = useRef(null);
  
  // Document Image Editor
  const [docEditorOpen, setDocEditorOpen] = useState(false);
  const [docEditorType, setDocEditorType] = useState(null); // 'identity_card' | 'driver_license' | 'passport'
  
  // OCR Result Modal
  const [ocrModalOpen, setOcrModalOpen] = useState(false);
  const [ocrModalData, setOcrModalData] = useState(null);
  
  // Finance
  const [finance, setFinance] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [potDetailDialog, setPotDetailDialog] = useState(false);
  const [financeFilter, setFinanceFilter] = useState('all'); // all, month, year, range, lastMonth
  const [financeMonth, setFinanceMonth] = useState(new Date().getMonth() + 1);
  const [financeYear, setFinanceYear] = useState(new Date().getFullYear());
  const [financeDateFrom, setFinanceDateFrom] = useState('');
  const [financeDateTo, setFinanceDateTo] = useState('');
  
  // Packages (Paketler)
  const [packages, setPackages] = useState(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesRegions, setPackagesRegions] = useState([]);
  const [packagesDateRange, setPackagesDateRange] = useState(null);
  const [packageStartDate, setPackageStartDate] = useState('');
  const [packageEndDate, setPackageEndDate] = useState('');
  const [packageRegionFilter, setPackageRegionFilter] = useState('');
  const [selectedPackagePreset, setSelectedPackagePreset] = useState('');
  
  // Filtered finance data
  const filteredFinance = useMemo(() => {
    if (!finance) return null;
    
    let filteredEntries = [...finance.entries];
    
    if (financeFilter !== 'all') {
      filteredEntries = finance.entries.filter(entry => {
        const entryDate = new Date(entry.date);
        const entryMonth = entryDate.getMonth() + 1;
        const entryYear = entryDate.getFullYear();
        
        if (financeFilter === 'month') {
          return entryMonth === financeMonth && entryYear === financeYear;
        } else if (financeFilter === 'year') {
          return entryYear === financeYear;
        } else if (financeFilter === 'lastMonth') {
          const lastMonth = new Date();
          lastMonth.setMonth(lastMonth.getMonth() - 1);
          return entryMonth === (lastMonth.getMonth() + 1) && entryYear === lastMonth.getFullYear();
        } else if (financeFilter === 'range' && financeDateFrom && financeDateTo) {
          const from = new Date(financeDateFrom);
          const to = new Date(financeDateTo);
          return entryDate >= from && entryDate <= to;
        }
        return true;
      });
    }
    
    // Recalculate summary for filtered entries
    let total_avans = 0, total_debt = 0, total_paid = 0, total_bonus = 0;
    let pot_plus = 0, pot_minus = 0, laadvergoeding = 0, total_deductions = 0;
    
    filteredEntries.forEach(entry => {
      const amount = entry.amount;
      const type = entry.entry_type;
      
      if (type === 'avans') { total_avans += amount; total_debt += amount; }
      else if (type === 'borc') { total_debt += amount; }
      else if (type === 'odeme') { total_paid += amount; }
      else if (type === 'kesinti') { total_deductions += amount; }
      else if (type === 'prim') { total_bonus += amount; }
      else if (type === 'pot_plus') { pot_plus += amount; }
      else if (type === 'pot_minus') { pot_minus += amount; }
      else if (type === 'laadvergoeding') { laadvergoeding += amount; }
    });
    
    return {
      entries: filteredEntries,
      summary: {
        total_avans,
        total_debt,
        total_paid,
        total_bonus,
        total_deductions,
        pot_plus,
        pot_minus,
        pot_balance: pot_plus - pot_minus,
        laadvergoeding,
        net_balance: total_paid - total_debt + total_bonus - total_deductions + (pot_plus - pot_minus) + laadvergoeding
      }
    };
  }, [finance, financeFilter, financeMonth, financeYear, financeDateFrom, financeDateTo]);

  // History
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Loonbriefs (Payslips)
  const [loonbriefs, setLoonbriefs] = useState([]);
  const [loonbriefsLoading, setLoonbriefsLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    naam: '',
    voornaam: '',
    geboortedatum: '',
    geboorteplaats: '',
    nationaliteit: '',
    rijksregisternummer: '',
    burgerlijke_staat: '',
    straat: '',
    huisnummer: '',
    postcode: '',
    woonplaats: '',
    land: 'België',
    gsm: '',
    email: '',
    bankrekening_iban: '',
    bloedgroep: '',
    fiscale_gezinssituatie: {
      kinderen_ten_laste: false,
      aantal_kinderen: null,
      echtgenoot_ten_laste: false,
      andere_personen_ten_laste: false,
      aantal_andere_personen: null
    },
    id_expiry_date: '',
    license_expiry_date: '',
    driver_attest_expiry_date: ''
  });
  
  // OCR state
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  // Delete confirmation
  const [deleteDoc, setDeleteDoc] = useState(null);
  
  // Document preview (unified - handles both images and PDFs via DocumentPreviewModal)
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchMetadata();
    fetchDocuments(); // Fetch documents on initial load for profile photo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'finance' && !finance) {
      fetchFinance();
    } else if (activeTab === 'history' && !history) {
      fetchHistory();
    } else if (activeTab === 'notes') {
      fetchMyNotes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'notes') fetchMyNotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesShowArchived]);

  // Language change handler
  const handleLanguageChange = async (lang) => {
    setSavingLanguage(true);
    try {
      await updateLanguage(lang);
      setLanguage(lang);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error('Dil güncellenemedi');
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleIdleTimeoutSave = async (value) => {
    setIdleTimeoutSaving(true);
    try {
      await api.put('/me/idle-timeout', { idle_timeout_minutes: value });
      toast.success(t('common.success'));
      const { fetchUser } = useAuthStore.getState();
      if (fetchUser) fetchUser();
    } catch {
      toast.error(t('common.error') || 'Error');
    } finally {
      setIdleTimeoutSaving(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.new_password_confirm) {
      toast.error(t('profile.passwordMismatch') || 'Şifreler eşleşmiyor');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.put('/me/password', passwordForm);
      toast.success(t('profile.passwordChanged') || 'Şifre başarıyla değiştirildi');
      setPasswordForm({ current_password: '', new_password: '', new_password_confirm: '' });
      setShowPasswordForm(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('profile.incorrectPassword') || 'Mevcut şifre yanlış');
      } else {
        toast.error('Şifre değiştirilemedi');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await api.get('/employee-profile/metadata');
      if (response.data.success) {
        setMetadata(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employee-profile/me');
      if (response.data.success) {
        const data = response.data.data;
        setProfileExists(data.exists);
        setProfileCompletion(data.profile_completion_percentage);
        
        if (data.profile) {
          setProfile(data.profile);
          setFormData({
            ...formData,
            ...data.profile,
            fiscale_gezinssituatie: data.profile.fiscale_gezinssituatie || formData.fiscale_gezinssituatie
          });
        } else if (user) {
          // Pre-fill from user data
          // Parse full_name into first and last name
          const nameParts = (user.full_name || '').split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          setFormData(prev => ({
            ...prev,
            voornaam: firstName,
            naam: lastName,
            email: user.email || '',
            gsm: user.gsm || user.phone || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Profil yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setDocumentsLoading(true);
      // Use main document system - get documents assigned to current user
      const response = await api.get('/documents/my');
      // Main document API returns array directly
      const docs = Array.isArray(response.data) ? response.data : (response.data.data?.documents || []);
      setDocuments(docs);
      
      // Find profile photo from documents and load it with auth. Use the
      // MOST-RECENT one OWNED by the current user (the API can return
      // documents with `access_level: "all"` belonging to other admins —
      // see the same fix in DashboardLayout.jsx for context).
      const photoDocs = docs
        .filter((d) => d.document_type === 'profile_photo')
        .filter((d) => (
          d.uploaded_by === user?.id ||
          (Array.isArray(d.assigned_to) && d.assigned_to.includes(user?.id))
        ))
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
      const photoDoc = photoDocs[0];
      if (photoDoc) {
        try {
          // Şub 27, 2026 — Direct CDN URL ile profil fotoğrafı — anında yükleme
          const { url } = await getPreviewUrl(api, photoDoc.id, true);
          if (url) setProfilePhoto(url);
        } catch (photoError) {
          console.error('Error loading profile photo:', photoError);
        }
      }

      // Cover photo support was removed per user request; profile avatar only.
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Fetch the current user's firm name (for header) and any PostNL codes
  // that have been assigned to them. Runs once per mount.
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        if (user?.firm_id) {
          const r = await api.get(`/firms/${user.firm_id}`);
          if (r.data?.success) setFirmName(r.data.data?.name || null);
        }
      } catch { /* silent */ }
      try {
        const r2 = await api.get(`/postnl/codes/by-user/${user.id}`);
        setMyPostnl(r2.data?.data || []);
      } catch { /* silent — may be 403 for non-admin employees; fine */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.firm_id]);

  const fetchFinance = async () => {
    try {
      setFinanceLoading(true);
      const response = await api.get(`/employee-profile/finance/${user.id}`);
      if (response.data.success) {
        setFinance(response.data.data);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setFinance({ hidden: true });
      } else {
        console.error('Error fetching finance:', error);
      }
    } finally {
      setFinanceLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get(`/employee-profile/history/${user.id}`);
      if (response.data.success) {
        setHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchMyNotes = async () => {
    try {
      setNotesLoading(true);
      const res = await api.get(`/sticky-notes?include_archived=${notesShowArchived}`);
      if (res.data?.success) setMyNotes(res.data.data || []);
    } catch {} finally { setNotesLoading(false); }
  };

  const handleNoteUnarchive = async (id) => {
    try {
      await api.put(`/sticky-notes/${id}`, { archived: false });
      toast.success('Not geri alındı');
      fetchMyNotes();
    } catch { toast.error('Hata'); }
  };

  const handleNoteDelete = async (id) => {
    try {
      await api.delete(`/sticky-notes/${id}`);
      toast.success('Not kalıcı olarak silindi');
      fetchMyNotes();
    } catch { toast.error('Hata'); }
  };

  const fetchLoonbriefs = async () => {
    try {
      setLoonbriefsLoading(true);
      const response = await api.get(`/loonbrief/user/${user.id}`);
      if (response.data.success) {
        setLoonbriefs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching loonbriefs:', error);
    } finally {
      setLoonbriefsLoading(false);
    }
  };

  const handleDownloadLoonbrief = async (docId, fileName) => {
    const ok = await downloadDocument(api, { id: docId, original_name: fileName || 'loonbrief.pdf' });
    if (!ok) toast.error('Dosya indirilirken hata oluştu');
  };

  const handlePreviewLoonbrief = (doc) => {
    // Use unified DocumentPreviewModal for loonbriefs (PDFs)
    setPreviewDoc({
      id: doc.id,
      file_name: doc.file_name,
      file_type: 'application/pdf'
    });
  };

  const fetchPackagesRegions = async () => {
    try {
      const response = await api.get(`/loonbrief/user-packages-regions/${user.id}`);
      if (response.data.success) {
        setPackagesRegions(response.data.data.regions || []);
        setPackagesDateRange(response.data.data.date_range);
      }
    } catch (error) {
      console.error('Error fetching packages regions:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const params = new URLSearchParams();
      if (packageStartDate) params.append('start_date', packageStartDate);
      if (packageEndDate) params.append('end_date', packageEndDate);
      if (packageRegionFilter) params.append('region', packageRegionFilter);
      
      const response = await api.get(`/loonbrief/user-packages/${user.id}?${params.toString()}`);
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const response = await api.post('/employee-profile/me', formData);
      if (response.data.success) {
        toast.success(t('employeeProfile.profileSaved'));
        fetchProfile();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('employeeProfile.profileSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadProfilePhoto = async (blob) => {
    try {
      setUploadingPhoto(true);
      const formDataUpload = new FormData();
      formDataUpload.append('files', blob, 'profile-photo.jpg');
      formDataUpload.append('file_names', 'Profil Fotoğrafı');
      formDataUpload.append('category', 'PERSONAL');
      formDataUpload.append('assigned_to', user.id);
      formDataUpload.append('document_type', 'profile_photo');

      const response = await api.post('/documents/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(t('employeeProfile.photoUploaded') || 'Profil fotoğrafı yüklendi');
        // Revoke the previous blob URL (if any) to prevent a memory leak on
        // repeated uploads.
        if (profilePhoto && profilePhoto.startsWith('blob:')) {
          URL.revokeObjectURL(profilePhoto);
        }
        // Create local URL for immediate display
        const localUrl = URL.createObjectURL(blob);
        setProfilePhoto(localUrl);
        // Notify the layout (header + sidebar avatars) to re-fetch.
        window.dispatchEvent(new CustomEvent('profile-photo-updated'));
        // Refresh documents to get the new photo
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error(t('employeeProfile.photoUploadError') || 'Fotoğraf yüklenemedi');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !docForm.document_type) {
      toast.error(t('employeeProfile.selectFileAndType'));
      return;
    }

    try {
      setUploadingDoc(true);
      const formDataUpload = new FormData();
      formDataUpload.append('files', selectedFile);
      // Use document type as custom file name
      const displayName = t(`employeeProfile.${DOCUMENT_TYPE_KEYS[docForm.document_type] || 'other'}`);
      formDataUpload.append('file_names', displayName);
      formDataUpload.append('category', 'PERSONAL');
      // Assign to current user only
      formDataUpload.append('assigned_to', user.id);

      const response = await api.post('/documents/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(t('employeeProfile.documentUploaded'));
        setUploadDialog(false);
        setSelectedFile(null);
        setDocForm({ document_type: '', document_number: '', issue_date: '', expiry_date: '', notes: '' });
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(t('employeeProfile.documentUploadError'));
    } finally {
      setUploadingDoc(false);
    }
  };

  // Handle document preview - uses unified DocumentPreviewModal for all file types
  const handleDocPreview = (doc) => {
    if (!doc) return;
    setPreviewDoc(doc);
  };

  // Handle document download or open in new tab
  const handleDocDownload = async (doc, openInNewTab = false) => {
    if (!doc) return;
    if (openInNewTab) {
      // Şub 27, 2026 — Direct CDN URL new tab'de anında açar
      try {
        const { url } = await getPreviewUrl(api, doc.id, true);
        if (url) window.open(url, '_blank');
      } catch (error) {
        console.error('Error opening document:', error);
        toast.error(t('employeeProfile.downloadError') || 'Açma hatası');
      }
      return;
    }
    const ok = await downloadDocument(api, doc);
    if (!ok) toast.error(t('employeeProfile.downloadError') || 'İndirme hatası');
  };

  // OCR function to extract dates from ID card or driver's license
  const extractDatesFromImage = async (file, docType) => {
    const ocrToastId = toast.loading('🔍 OCR: Belge taranıyor, lütfen bekleyin...', { duration: 60000 });
    
    try {
      setOcrProcessing(true);
      
      // Create image URL for Tesseract
      const imageUrl = URL.createObjectURL(file);
      
      // Run OCR with Tesseract.js - Support for multiple languages (EN, TR, NL, FR, DE)
      const result = await Tesseract.recognize(
        imageUrl,
        'eng+tur+nld+fra+deu',
        {
          logger: (m) => {
            if (m.status === 'recognizing text' && m.progress) {
              toast.loading(`🔍 OCR: %${Math.round(m.progress * 100)} tamamlandı...`, { id: ocrToastId });
            }
          }
        }
      );
      
      URL.revokeObjectURL(imageUrl);
      
      const text = result.data.text;
      console.log('OCR Result:', text);
      
      // Date patterns to search for (various formats)
      const datePatterns = [
        // DD.MM.YYYY or DD/MM/YYYY or DD-MM-YYYY (most common European format)
        /(\d{2})[.\/-](\d{2})[.\/-](\d{4})/g,
        // YYYY.MM.DD or YYYY/MM/DD or YYYY-MM-DD (ISO format)
        /(\d{4})[.\/-](\d{2})[.\/-](\d{2})/g,
        // DD MM YYYY (space separated - Belgian ID format!)
        /(\d{2})\s+(\d{2})\s+(\d{4})/g,
        // DD MMM YYYY (e.g., 15 JAN 2025) - Multiple languages
        /(\d{1,2})\s*(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC|OCA|ŞUB|NİS|HAZ|TEM|AĞU|EYL|EKİ|KAS|ARA|JANV?|FÉVR?|MARS|AVR|MAI|JUIN|JUIL|AOÛT|SEPT|OCTO?|NOVE?|DÉCE?|MRT|MEI|OKT)[A-ZİÉÛ]*\.?\s*(\d{4})/gi,
        // D.MM.YYYY or D/MM/YYYY (single digit day - common in Belgium)
        /(\d{1})[.\/-](\d{2})[.\/-](\d{4})/g,
        // DD.M.YYYY or DD/M/YYYY (single digit month)
        /(\d{2})[.\/-](\d{1})[.\/-](\d{4})/g
      ];
      
      // Month names in multiple languages (Dutch, French, German, Turkish, English)
      const monthMap = {
        // English
        'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04', 'MAY': '05', 'JUN': '06',
        'JUL': '07', 'AUG': '08', 'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12',
        // Turkish
        'OCA': '01', 'ŞUB': '02', 'NİS': '04', 'HAZ': '06', 'TEM': '07', 'AĞU': '08',
        'EYL': '09', 'EKİ': '10', 'KAS': '11', 'ARA': '12',
        // French
        'JANV': '01', 'FÉVR': '02', 'MARS': '03', 'AVR': '04', 'MAI': '05', 'JUIN': '06',
        'JUIL': '07', 'AOÛT': '08', 'SEPT': '09', 'OCTO': '10', 'NOVE': '11', 'DÉCE': '12',
        // Dutch (Belgium)
        'MRT': '03', 'MEI': '05', 'OKT': '10',
        // German
        'MÄR': '03', 'MRZ': '03', 'OKT': '10', 'DEZ': '12'
      };
      
      const foundDates = [];
      
      // First, try to find dates near Belgian ID/License keywords
      // For driver's license: 4a = issue date, 4b = expiry date
      const expiryKeywords = ['GELDIG TOT', 'VALABLE', 'GÜLTIG BIS', 'VALID UNTIL', 'EXPIRY', 'VERVALT', '4B', '4B.'];
      const birthKeywords = ['GEBOORTEDATUM', 'DATE DE NAISSANCE', 'BIRTH', '3.'];
      let expiryContext = null;
      let foundExpiryKeyword = null;
      
      for (const keyword of expiryKeywords) {
        const keywordIndex = text.toUpperCase().indexOf(keyword);
        if (keywordIndex !== -1) {
          // Extract text around keyword (50 chars after)
          expiryContext = text.substring(keywordIndex, keywordIndex + 50);
          foundExpiryKeyword = keyword;
          console.log(`Found expiry keyword "${keyword}" context:`, expiryContext);
          break;
        }
      }
      
      // Search for dates with first two patterns (DD.MM.YYYY and YYYY.MM.DD)
      for (let i = 0; i < 2; i++) {
        const pattern = datePatterns[i];
        pattern.lastIndex = 0; // Reset regex state for consistent results
        let match;
        while ((match = pattern.exec(text)) !== null) {
          let year, month, day;
          if (i === 0) {
            // DD.MM.YYYY format
            day = match[1];
            month = match[2];
            year = match[3];
          } else {
            // YYYY.MM.DD format
            year = match[1];
            month = match[2];
            day = match[3];
          }
          
          const yearNum = parseInt(year);
          const monthNum = parseInt(month);
          const dayNum = parseInt(day);
          
          // Validate date components
          if (yearNum >= 1950 && yearNum <= 2100 && 
              monthNum >= 1 && monthNum <= 12 && 
              dayNum >= 1 && dayNum <= 31) {
            foundDates.push({
              date: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
              yearNum,
              isNearExpiry: expiryContext && expiryContext.includes(match[0])
            });
          }
        }
      }
      
      // Search for space-separated dates (DD MM YYYY) - Belgian ID format - pattern index 2
      let matchSpace;
      const patternSpace = datePatterns[2];
      patternSpace.lastIndex = 0; // Reset regex state
      while ((matchSpace = patternSpace.exec(text)) !== null) {
        const day = matchSpace[1];
        const month = matchSpace[2];
        const year = matchSpace[3];
        const yearNum = parseInt(year);
        const monthNum = parseInt(month);
        const dayNum = parseInt(day);
        
        if (yearNum >= 1950 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
          foundDates.push({
            date: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
            yearNum,
            isNearExpiry: expiryContext && expiryContext.includes(matchSpace[0])
          });
          console.log('Found space-separated date:', `${day} ${month} ${year}`);
        }
      }
      
      // Search for single digit day pattern (D.MM.YYYY) - pattern index 4
      let match3;
      const pattern3 = datePatterns[4];
      pattern3.lastIndex = 0; // Reset regex state
      while ((match3 = pattern3.exec(text)) !== null) {
        const day = match3[1].padStart(2, '0');
        const month = match3[2];
        const year = match3[3];
        const yearNum = parseInt(year);
        const monthNum = parseInt(month);
        
        if (yearNum >= 1950 && yearNum <= 2100 && monthNum >= 1 && monthNum <= 12) {
          foundDates.push({
            date: `${year}-${month}-${day}`,
            yearNum,
            isNearExpiry: expiryContext && expiryContext.includes(match3[0])
          });
        }
      }
      
      // Search for single digit month pattern (DD.M.YYYY) - pattern index 5
      let match4;
      const pattern4 = datePatterns[5];
      pattern4.lastIndex = 0; // Reset regex state
      while ((match4 = pattern4.exec(text)) !== null) {
        const day = match4[1];
        const month = match4[2].padStart(2, '0');
        const year = match4[3];
        const yearNum = parseInt(year);
        const dayNum = parseInt(day);
        
        if (yearNum >= 1950 && yearNum <= 2100 && dayNum >= 1 && dayNum <= 31) {
          foundDates.push({
            date: `${year}-${month}-${day}`,
            yearNum,
            isNearExpiry: expiryContext && expiryContext.includes(match4[0])
          });
        }
      }
      
      // Search for DD MMM YYYY format (multiple languages) - pattern index 3
      let matchText;
      datePatterns[3].lastIndex = 0; // Reset regex state
      while ((matchText = datePatterns[3].exec(text)) !== null) {
        const day = matchText[1].padStart(2, '0');
        const monthStr = matchText[2].toUpperCase().replace(/[.]/g, '').substring(0, 4);
        // Try to find month in map (try 4 chars, then 3 chars)
        let monthNum = monthMap[monthStr] || monthMap[monthStr.substring(0, 3)];
        const year = matchText[3];
        
        if (monthNum) {
          const yearNum = parseInt(year);
          if (yearNum >= 1950 && yearNum <= 2100) {
            foundDates.push({
              date: `${year}-${monthNum}-${day}`,
              yearNum,
              isNearExpiry: expiryContext && expiryContext.includes(matchText[0])
            });
          }
        }
      }
      
      // Try to extract from MRZ (Machine Readable Zone) - Belgian eID has YYMMDD format
      const mrzPattern = /([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])/g;
      const mrzMatches = text.match(mrzPattern);
      if (mrzMatches) {
        for (const mrzDate of mrzMatches) {
          const yy = parseInt(mrzDate.substring(0, 2));
          const mm = mrzDate.substring(2, 4);
          const dd = mrzDate.substring(4, 6);
          // Determine century (if yy > 50, assume 1900s, else 2000s)
          const year = yy > 50 ? `19${mrzDate.substring(0, 2)}` : `20${mrzDate.substring(0, 2)}`;
          const yearNum = parseInt(year);
          
          if (yearNum >= 1950 && yearNum <= 2100) {
            foundDates.push({
              date: `${year}-${mm}-${dd}`,
              yearNum,
              fromMRZ: true
            });
          }
        }
      }
      
      // Remove duplicates and sort by date
      const uniqueDates = [...new Set(foundDates.map(d => d.date))].map(date => {
        const found = foundDates.find(d => d.date === date);
        return {
          date,
          yearNum: parseInt(date.split('-')[0]),
          isNearExpiry: found?.isNearExpiry || false,
          fromMRZ: found?.fromMRZ || false
        };
      });
      
      console.log('All found dates:', uniqueDates);
      
      // Determine which dates are birth date and expiry date
      // Birth date is usually in the past (before current year)
      // Expiry date: For driver's license, take the FURTHEST future date (4b > 4a)
      const currentYear = new Date().getFullYear();
      
      const pastDates = uniqueDates.filter(d => d.yearNum < currentYear);
      const futureDates = uniqueDates.filter(d => d.yearNum >= currentYear);
      
      let extractedBirthDate = null;
      let extractedExpiryDate = null;
      
      // First check if any date is near expiry keywords (like "4b" or "GELDIG TOT")
      const expiryKeywordDate = uniqueDates.find(d => d.isNearExpiry);
      if (expiryKeywordDate) {
        extractedExpiryDate = expiryKeywordDate.date;
        console.log('Found expiry date near keyword:', extractedExpiryDate);
      }
      
      // Birth date: oldest date in the past
      if (pastDates.length > 0) {
        pastDates.sort((a, b) => new Date(a.date) - new Date(b.date));
        extractedBirthDate = pastDates[0].date;
      }
      
      // Expiry date for driver's license: take the FURTHEST future date (4b is always > 4a)
      // This is different from ID card where we take the nearest future date
      if (!extractedExpiryDate && futureDates.length > 0) {
        // Sort ascending to get the furthest date last, then take the last one
        futureDates.sort((a, b) => new Date(a.date) - new Date(b.date));
        // For driver's license with multiple future dates, take the FURTHEST one (4b)
        if (docType === 'driver_license' && futureDates.length > 1) {
          extractedExpiryDate = futureDates[futureDates.length - 1].date; // Furthest future date
          console.log('Driver license: taking furthest future date (4b):', extractedExpiryDate);
        } else {
          // For ID card or single future date, take the nearest/only one
          extractedExpiryDate = futureDates[futureDates.length - 1].date;
        }
      }
      
      // If still no expiry date but we have dates, take the most recent one that's not birth date
      if (!extractedExpiryDate && uniqueDates.length > 1) {
        const sortedDates = [...uniqueDates].sort((a, b) => new Date(b.date) - new Date(a.date));
        if (sortedDates[0].date !== extractedBirthDate) {
          extractedExpiryDate = sortedDates[0].date;
        } else if (sortedDates.length > 1) {
          extractedExpiryDate = sortedDates[1].date;
        }
      }
      
      // Dismiss loading toast
      toast.dismiss(ocrToastId);
      
      // Extract RIJKSREGISTERNR (Belgian national ID number)
      // Format: YY.MM.DD-XXX.YY or YY MM DD XXX YY or variations
      let extractedRijksregisternr = null;
      if (docType === 'identity_card') {
        const rrPatterns = [
          /(\d{2})[.\s](\d{2})[.\s](\d{2})[.\s-](\d{3})[.\s-](\d{2})/g,
          /(\d{2})\.(\d{2})\.(\d{2})[-\s](\d{3})\.(\d{2})/g,
        ];
        for (const rrPattern of rrPatterns) {
          rrPattern.lastIndex = 0;
          const rrMatch = rrPattern.exec(text);
          if (rrMatch) {
            extractedRijksregisternr = `${rrMatch[1]}.${rrMatch[2]}.${rrMatch[3]}-${rrMatch[4]}.${rrMatch[5]}`;
            console.log('Found RIJKSREGISTERNR:', extractedRijksregisternr);
            break;
          }
        }
        if (!extractedRijksregisternr) {
          const rrKeywords = ['RIJKSREGISTERNR', 'REGISTERNR', 'NAT.REG', 'Reg.Nr', 'CARTE', 'NR.'];
          for (const kw of rrKeywords) {
            const kwIdx = text.toUpperCase().indexOf(kw);
            if (kwIdx !== -1) {
              const nearby = text.substring(kwIdx, kwIdx + 60);
              const nearbyMatch = nearby.match(/(\d{2})[.\s](\d{2})[.\s](\d{2})[.\s-](\d{3})[.\s-](\d{2})/);
              if (nearbyMatch) {
                extractedRijksregisternr = `${nearbyMatch[1]}.${nearbyMatch[2]}.${nearbyMatch[3]}-${nearbyMatch[4]}.${nearbyMatch[5]}`;
                console.log('Found RIJKSREGISTERNR near keyword:', extractedRijksregisternr);
                break;
              }
            }
          }
        }
      }
      
      setOcrResult({
        text,
        birthDate: extractedBirthDate,
        expiryDate: extractedExpiryDate,
        rijksregisternr: extractedRijksregisternr,
        allDates: uniqueDates.map(d => d.date)
      });
      
      // Show OCR Result Modal for user confirmation
      if (extractedExpiryDate || extractedBirthDate || extractedRijksregisternr) {
        toast.success(
          `OCR Tamamlandi!\n` +
          (extractedExpiryDate ? `Son Kullanma: ${new Date(extractedExpiryDate).toLocaleDateString('tr-TR')}\n` : '') +
          (extractedBirthDate ? `Dogum Tarihi: ${new Date(extractedBirthDate).toLocaleDateString('tr-TR')}\n` : '') +
          (extractedRijksregisternr ? `Rijksregisternr: ${extractedRijksregisternr}` : ''),
          { duration: 5000 }
        );
        
        setOcrModalData({
          docType,
          text: text.substring(0, 500),
          birthDate: extractedBirthDate,
          expiryDate: extractedExpiryDate,
          rijksregisternr: extractedRijksregisternr,
          allDates: uniqueDates.map(d => d.date)
        });
        setOcrModalOpen(true);
      } else if (uniqueDates.length > 0) {
        toast.warning(
          `⚠️ OCR: ${uniqueDates.length} tarih bulundu ama son kullanma tarihi belirlenemedi.\n` +
          `Bulunan: ${uniqueDates.map(d => new Date(d.date).toLocaleDateString('tr-TR')).join(', ')}`,
          { duration: 6000 }
        );
      } else {
        toast.error('❌ OCR: Belgede tarih bulunamadı. Lütfen daha net bir görüntü yükleyin veya manuel girin.', { duration: 5000 });
      }
      
    } catch (error) {
      console.error('OCR Error:', error);
      toast.dismiss(ocrToastId);
      toast.error('❌ OCR Hatası: ' + (error.message || 'Bilinmeyen hata'), { duration: 4000 });
    } finally {
      setOcrProcessing(false);
    }
  };

  // Handle OCR result confirmation
  const handleOcrConfirm = () => {
    if (!ocrModalData) return;
    
    const { docType, expiryDate, birthDate, rijksregisternr } = ocrModalData;
    const updates = {};
    const messages = [];
    
    if (docType === 'identity_card' && expiryDate) {
      updates.id_expiry_date = expiryDate;
      messages.push(`Kimlik son kullanma tarihi: ${new Date(expiryDate).toLocaleDateString('tr-TR')}`);
    } else if (docType === 'driver_license' && expiryDate) {
      updates.license_expiry_date = expiryDate;
      messages.push(`Ehliyet son kullanma tarihi: ${new Date(expiryDate).toLocaleDateString('tr-TR')}`);
    } else if (docType === 'bestuurdersattest' && expiryDate) {
      updates.driver_attest_expiry_date = expiryDate;
      messages.push(`Sürücü Belgesi son kullanma tarihi: ${new Date(expiryDate).toLocaleDateString('tr-TR')}`);
    }
    
    if (birthDate && docType !== 'bestuurdersattest') {
      updates.geboortedatum = birthDate;
      messages.push(`Doğum tarihi: ${new Date(birthDate).toLocaleDateString('tr-TR')}`);
    }

    if (rijksregisternr && docType !== 'bestuurdersattest') {
      updates.rijksregisternummer = rijksregisternr;
      messages.push(`Rijksregisternr: ${rijksregisternr}`);
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      toast.success(`OCR verileri uygulandı: ${messages.join(', ')}`);
    }
    
    setOcrModalOpen(false);
    setOcrModalData(null);
  };

  // Open Document Image Editor for ID/License upload
  const openDocumentEditor = (docType) => {
    setDocEditorType(docType);
    setDocEditorOpen(true);
  };

  // Handle file from Document Image Editor
  const handleDocumentEditorComplete = async (file) => {
    if (!file || !docEditorType) return;
    await handleUploadImportantDoc(file, docEditorType);
    setDocEditorType(null);
  };

  // Upload important document (ID, Passport, Driver's License) with OCR support
  const handleUploadImportantDoc = async (file, docType) => {
    if (!file) return;
    
    try {
      setUploadingImportantDoc(true);
      
      // If it's an image file (ID card or driver's license), run OCR first
      const isImage = file.type.startsWith('image/');
      if (isImage && (docType === 'identity_card' || docType === 'driver_license')) {
        // Run OCR to extract dates
        await extractDatesFromImage(file, docType);
      }
      
      // Upload the document
      const formDataUpload = new FormData();
      formDataUpload.append('files', file);
      
      // Map document type to display name
      const docNames = {
        'identity_card': t('employeeProfile.identityCard') || 'Kimlik Kartı',
        'passport': t('employeeProfile.passport') || 'Pasaport',
        'driver_license': t('employeeProfile.driverLicense') || 'Ehliyet'
      };
      
      formDataUpload.append('file_names', docNames[docType] || docType);
      formDataUpload.append('category', 'PERSONAL');
      formDataUpload.append('assigned_to', user.id);
      formDataUpload.append('document_type', docType);

      const response = await api.post('/documents/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(t('employeeProfile.documentUploaded') || 'Belge yüklendi');
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading important document:', error);
      toast.error(t('employeeProfile.documentUploadError') || 'Belge yüklenemedi');
    } finally {
      setUploadingImportantDoc(false);
      setImportantDocType(null);
    }
  };

  const handleImportantDocSelect = (e, docType) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadImportantDoc(file, docType);
    }
    // Reset input
    e.target.value = '';
  };

  const handleDownloadDocument = async (doc) => {
    const ok = await downloadDocument(api, doc);
    if (!ok) toast.error(t('employeeProfile.downloadError'));
  };

  const handleDeleteDocument = async () => {
    if (!deleteDoc) return;
    try {
      const response = await api.delete(`/documents/${deleteDoc.id}`);
      if (response.data.success) {
        toast.success(t('employeeProfile.documentDeleted'));
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t('employeeProfile.documentDeleteError'));
    } finally {
      setDeleteDoc(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('nl-BE');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compact header — avatar (click to change), name, email, firm, PostNL chips */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          {/* Profile Photo — click/tap to change. */}
          <div className="relative shrink-0 group">
            {/* View-only avatar for regular users. Super Admin can click to change. */}
            <div
              className="relative h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-3xl font-bold overflow-hidden border-4 border-white dark:border-slate-900 shadow-lg"
              data-testid="profile-photo-view"
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                formData.voornaam?.[0]?.toUpperCase() || user?.full_name?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            {user?.role === 'SUPER_ADMIN' && (
              <>
                <button
                  type="button"
                  onClick={() => setPhotoCropperOpen(true)}
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  data-testid="self-change-photo-btn"
                  title={t('employeeProfile.changePhoto') || 'Fotoğrafı Değiştir'}
                >
                  <Camera className="h-6 w-6 text-white" />
                </button>
                {uploadingPhoto && (
                  <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 truncate">
              {(() => {
                const full = `${formData.voornaam || ''} ${formData.naam || ''}`.trim();
                if (full) return full;
                if (user?.full_name) return user.full_name;
                // Role-based friendly fallback (Turkish)
                const roleLabels = {
                  SUPER_ADMIN: 'Sistem Admin',
                  FULL_ADMIN: 'Admin',
                  FIRM_ADMIN: 'Firma Admin',
                  EMPLOYEE: 'Çalışan',
                };
                return roleLabels[user?.role] || (user?.email ? user.email.split('@')[0] : 'Kullanıcı');
              })()}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
            {firmName && (
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 inline-flex items-center gap-1.5">
                <Building2 className="h-3 w-3" />
                <span className="font-medium">{firmName}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.profileCompletion')}</p>
            <div className="flex items-center gap-2 justify-end">
              <Progress value={profileCompletion} className="w-32 h-2" />
              <span className="text-sm font-medium">{profileCompletion}%</span>
            </div>
          </div>
          {profileCompletion === 100 ? (
            <Badge className="bg-green-100 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" /> {t('employeeProfile.complete')}
            </Badge>
          ) : (
            <Badge className="bg-yellow-100 text-yellow-700">
              <AlertCircle className="h-3 w-3 mr-1" /> {t('employeeProfile.incomplete')}
            </Badge>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full lg:w-auto">
          <TabsTrigger value="personal" className="gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">{t('employeeProfile.personalInfo')}</span>
            <span className="sm:hidden">Bilgiler</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.settings') || 'Hesap Ayarları'}</span>
            <span className="sm:hidden">Ayarlar</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.security') || 'Güvenlik'}</span>
            <span className="sm:hidden">Güvenlik</span>
          </TabsTrigger>
          <TabsTrigger value="notes" className="gap-2">
            <StickyNote className="h-4 w-4" />
            <span className="hidden sm:inline">Notlarım</span>
            <span className="sm:hidden">Notlar</span>
          </TabsTrigger>
        </TabsList>

        {/* Personal Info Tab */}
        <TabsContent value="personal" className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                {t('employeeProfile.personalInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="voornaam">{t('employeeProfile.firstName')} *</Label>
                  <Input
                    id="voornaam"
                    value={formData.voornaam}
                    onChange={(e) => setFormData({ ...formData, voornaam: e.target.value })}
                    placeholder="Jan"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="naam">{t('employeeProfile.lastName')} *</Label>
                  <Input
                    id="naam"
                    value={formData.naam}
                    onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                    placeholder="Janssen"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="geboortedatum">{t('employeeProfile.birthDate')} *</Label>
                  <Input
                    id="geboortedatum"
                    type="date"
                    value={formData.geboortedatum}
                    onChange={(e) => setFormData({ ...formData, geboortedatum: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="geboorteplaats">{t('employeeProfile.birthPlace')}</Label>
                  <Input
                    id="geboorteplaats"
                    value={formData.geboorteplaats}
                    onChange={(e) => setFormData({ ...formData, geboorteplaats: e.target.value })}
                    placeholder="Brussel"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nationaliteit">{t('employeeProfile.nationality')} *</Label>
                  <Select
                    value={formData.nationaliteit || ''}
                    onValueChange={(value) => setFormData({ ...formData, nationaliteit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('common.select') || 'Seçiniz'} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {NATIONALITIES.map((nat) => (
                        <SelectItem 
                          key={nat.code} 
                          value={nat.code} 
                          disabled={nat.disabled}
                        >
                          {nat.flag} {nat[language] || nat.tr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rijksregisternummer">{t('employeeProfile.nationalId')}</Label>
                  <Input
                    id="rijksregisternummer"
                    data-testid="rijksregisternummer-input"
                    placeholder={getPlaceholder(formData.nationaliteit)}
                    value={formData.rijksregisternummer}
                    onChange={(e) => {
                      const cc = formData.nationaliteit;
                      const raw = stripDigits(e.target.value);
                      const max = getMaxDigits(cc);
                      if (raw.length <= max) {
                        const shouldFormat = (cc === 'BE' && raw.length === 11) || (cc === 'TR' && raw.length === 11);
                        setFormData({ ...formData, rijksregisternummer: shouldFormat ? formatNationalId(raw, cc) : e.target.value });
                      }
                    }}
                    className={(() => {
                      const cc = formData.nationaliteit;
                      const digits = stripDigits(formData.rijksregisternummer);
                      if (!digits) return '';
                      if ((cc === 'BE' || cc === 'TR') && digits.length < 11) return '';
                      const v = validateNationalId(formData.rijksregisternummer, cc, formData.geboortedatum);
                      if (!v.valid) return 'border-red-500 focus-visible:ring-red-500';
                      if (v.warning) return 'border-amber-500 focus-visible:ring-amber-500';
                      if (v.info) return 'border-green-500 focus-visible:ring-green-500';
                      return '';
                    })()}
                  />
                  {(() => {
                    const cc = formData.nationaliteit;
                    const digits = stripDigits(formData.rijksregisternummer);
                    if (!digits) return null;
                    if ((cc === 'BE' || cc === 'TR') && digits.length < 11) return null;
                    const result = validateNationalId(formData.rijksregisternummer, cc, formData.geboortedatum);
                    if (result.error) return <p className="text-xs text-red-600 mt-1" data-testid="rrn-error">{result.error}</p>;
                    if (result.warning) return <p className="text-xs text-amber-600 mt-1" data-testid="rrn-warning">{result.warning}</p>;
                    if (result.info) return <p className="text-xs text-green-600 mt-1" data-testid="rrn-valid">{result.info}</p>;
                    return null;
                  })()}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="burgerlijke_staat">{t('employeeProfile.maritalStatus')}</Label>
                  <Select
                    value={formData.burgerlijke_staat}
                    onValueChange={(v) => setFormData({ ...formData, burgerlijke_staat: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(MARITAL_STATUS_KEYS).map(([key, i18nKey]) => (
                        <SelectItem key={key} value={key}>{t(`employeeProfile.${i18nKey}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bloedgroep">{t('employeeProfile.bloodType')}</Label>
                  <Select
                    value={formData.bloedgroep}
                    onValueChange={(v) => setFormData({ ...formData, bloedgroep: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="..." />
                    </SelectTrigger>
                    <SelectContent>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="id_expiry_date" className="flex items-center gap-2">
                    <IdCard className="h-4 w-4 text-blue-500" />
                    {t('employeeProfile.idExpiryDate') || 'Kimlik Son Kullanma Tarihi'}
                  </Label>
                  <Input
                    id="id_expiry_date"
                    type="date"
                    value={formData.id_expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, id_expiry_date: e.target.value })}
                    disabled={ocrProcessing}
                  />
                  <p className="text-xs text-slate-500">{t('employeeProfile.ocrHint') || 'Kimlik yüklerken otomatik doldurulur'}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_expiry_date" className="flex items-center gap-2">
                    <Car className="h-4 w-4 text-green-500" />
                    {t('employeeProfile.licenseExpiryDate') || 'Ehliyet Son Kullanma Tarihi'}
                  </Label>
                  <Input
                    id="license_expiry_date"
                    type="date"
                    value={formData.license_expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, license_expiry_date: e.target.value })}
                    disabled={ocrProcessing}
                  />
                  <p className="text-xs text-slate-500">{t('employeeProfile.ocrHint') || 'Ehliyet yüklerken otomatik doldurulur'}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="driver_attest_expiry_date" className="flex items-center gap-2">
                    <FileCheck2 className="h-4 w-4 text-purple-500" />
                    Sürücü Belgesi (Bestuurdersattest) Bitiş Tarihi
                  </Label>
                  <Input
                    id="driver_attest_expiry_date"
                    type="date"
                    value={formData.driver_attest_expiry_date || ''}
                    onChange={(e) => setFormData({ ...formData, driver_attest_expiry_date: e.target.value })}
                    disabled={ocrProcessing}
                  />
                  <p className="text-xs text-slate-500">Bestuurdersattest yüklerken otomatik doldurulur. Yaklaşıyorsa bildirim alırsınız.</p>
                </div>
              </div>
              
              {/* OCR Processing Indicator */}
              {ocrProcessing && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    {t('employeeProfile.ocrProcessing') || 'Belge taranıyor, tarihler çıkarılıyor...'}
                  </span>
                </div>
              )}

              {/* PostNL Codes — only shown if the employee has any assigned.
                  Read-only chip list; management is done by admins via the
                  /dashboard/postnl-codes page or the admin profile view. */}
              {myPostnl.length > 0 && (
                <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-700" data-testid="employee-profile-postnl">
                  <Label className="flex items-center gap-2 mb-3">
                    <Package className="h-4 w-4 text-orange-600" />
                    <span>PostNL Kodları</span>
                    <span className="text-xs text-slate-400 font-normal">({myPostnl.length})</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {myPostnl.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-full border border-orange-200 bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-200"
                        title={`${c.firm_name || ''} · ${c.surname || ''} ${c.initials || ''}`.trim()}
                      >
                        <span className="font-mono font-semibold">{c.code}</span>
                        {(c.surname || c.initials) && (
                          <span className="text-xs opacity-80">{`${c.surname || ''} ${c.initials || ''}`.trim()}</span>
                        )}
                        {c.firm_name && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/70 dark:bg-slate-800/60 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                            {c.firm_name}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                {t('employeeProfile.addressInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2 lg:col-span-2">
                  <Label htmlFor="straat">{t('employeeProfile.street')} *</Label>
                  <Input
                    id="straat"
                    value={formData.straat}
                    onChange={(e) => setFormData({ ...formData, straat: e.target.value })}
                    placeholder="Koningsstraat"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="huisnummer">{t('employeeProfile.houseNumber')} *</Label>
                  <Input
                    id="huisnummer"
                    value={formData.huisnummer}
                    onChange={(e) => setFormData({ ...formData, huisnummer: e.target.value })}
                    placeholder="123"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postcode">{t('employeeProfile.postalCode')} *</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    placeholder="1000"
                  />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label htmlFor="woonplaats">{t('employeeProfile.city')} *</Label>
                  <Input
                    id="woonplaats"
                    value={formData.woonplaats}
                    onChange={(e) => setFormData({ ...formData, woonplaats: e.target.value })}
                    placeholder="Brussel"
                  />
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <Label htmlFor="land">{t('employeeProfile.country')} *</Label>
                  <Select
                    value={formData.land || ''}
                    onValueChange={(value) => setFormData({ ...formData, land: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('common.select') || 'Seçiniz'} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {COUNTRIES.map((country) => (
                        <SelectItem 
                          key={country.code} 
                          value={country.code} 
                          disabled={country.disabled}
                        >
                          {country.flag} {country[language] || country.tr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Bank */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="h-5 w-5 text-purple-600" />
                {t('employeeProfile.contactBankInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gsm">{t('employeeProfile.phone')} *</Label>
                  <Input
                    id="gsm"
                    value={formData.gsm}
                    onChange={(e) => setFormData({ ...formData, gsm: e.target.value })}
                    placeholder="+32 470 12 34 56"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('employeeProfile.emailAddress')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jan.janssen@email.be"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bankrekening_iban">{t('employeeProfile.bankAccount')} *</Label>
                  <Input
                    id="bankrekening_iban"
                    value={formData.bankrekening_iban}
                    onChange={(e) => setFormData({ ...formData, bankrekening_iban: e.target.value })}
                    placeholder="BE00 0000 0000 0000"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fiscal Family Situation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                {t('employeeProfile.fiscalInfo')}
              </CardTitle>
              <CardDescription>
                {t('employeeProfile.fiscalInfoDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Children */}
              <div className="space-y-3">
                <Label className="text-base font-medium">{t('employeeProfile.childrenDependent')}</Label>
                <RadioGroup
                  value={formData.fiscale_gezinssituatie.kinderen_ten_laste ? 'ja' : 'neen'}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    fiscale_gezinssituatie: {
                      ...formData.fiscale_gezinssituatie,
                      kinderen_ten_laste: v === 'ja',
                      aantal_kinderen: v === 'ja' ? formData.fiscale_gezinssituatie.aantal_kinderen : null
                    }
                  })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ja" id="kinderen-ja" />
                    <Label htmlFor="kinderen-ja">{t('common.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="neen" id="kinderen-neen" />
                    <Label htmlFor="kinderen-neen">{t('common.no')}</Label>
                  </div>
                </RadioGroup>
                {formData.fiscale_gezinssituatie.kinderen_ten_laste && (
                  <div className="ml-6 w-32">
                    <Label htmlFor="aantal_kinderen">{t('employeeProfile.numberOfChildren')}</Label>
                    <Input
                      id="aantal_kinderen"
                      type="number"
                      min="1"
                      value={formData.fiscale_gezinssituatie.aantal_kinderen || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        fiscale_gezinssituatie: {
                          ...formData.fiscale_gezinssituatie,
                          aantal_kinderen: parseInt(e.target.value) || null
                        }
                      })}
                    />
                  </div>
                )}
              </div>

              {/* Spouse */}
              <div className="space-y-3">
                <Label className="text-base font-medium">{t('employeeProfile.spouseDependent')}</Label>
                <RadioGroup
                  value={formData.fiscale_gezinssituatie.echtgenoot_ten_laste ? 'ja' : 'neen'}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    fiscale_gezinssituatie: {
                      ...formData.fiscale_gezinssituatie,
                      echtgenoot_ten_laste: v === 'ja'
                    }
                  })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ja" id="echtgenoot-ja" />
                    <Label htmlFor="echtgenoot-ja">{t('common.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="neen" id="echtgenoot-neen" />
                    <Label htmlFor="echtgenoot-neen">{t('common.no')}</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Other persons */}
              <div className="space-y-3">
                <Label className="text-base font-medium">{t('employeeProfile.otherDependents')}</Label>
                <RadioGroup
                  value={formData.fiscale_gezinssituatie.andere_personen_ten_laste ? 'ja' : 'neen'}
                  onValueChange={(v) => setFormData({
                    ...formData,
                    fiscale_gezinssituatie: {
                      ...formData.fiscale_gezinssituatie,
                      andere_personen_ten_laste: v === 'ja',
                      aantal_andere_personen: v === 'ja' ? formData.fiscale_gezinssituatie.aantal_andere_personen : null
                    }
                  })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ja" id="andere-ja" />
                    <Label htmlFor="andere-ja">{t('common.yes')}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="neen" id="andere-neen" />
                    <Label htmlFor="andere-neen">{t('common.no')}</Label>
                  </div>
                </RadioGroup>
                {formData.fiscale_gezinssituatie.andere_personen_ten_laste && (
                  <div className="ml-6 w-32">
                    <Label htmlFor="aantal_andere_personen">{t('employeeProfile.numberOfOtherDependents')}</Label>
                    <Input
                      id="aantal_andere_personen"
                      type="number"
                      min="1"
                      value={formData.fiscale_gezinssituatie.aantal_andere_personen || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        fiscale_gezinssituatie: {
                          ...formData.fiscale_gezinssituatie,
                          aantal_andere_personen: parseInt(e.target.value) || null
                        }
                      })}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSaveProfile} disabled={saving} size="lg">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {t('employeeProfile.saveProfile')}
            </Button>
          </div>

          {/* Atanmış araç(lar) — kullanıcının kendi profili: aktif plaka +
              geçmiş atamalar. */}
          {user?.id && <UserVehicleHistory userId={user.id} />}
          {user?.id && <UserAssignmentHistory userId={user.id} />}
        </TabsContent>

        {/* Account Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Account Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                {t('profile.accountInfo') || 'Hesap Bilgileri'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <div className="grid gap-0 divide-y divide-slate-100 dark:divide-slate-700">
                <div className="flex items-center gap-4 py-4">
                  <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    <Building2 className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.company') || 'Firma'}</p>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{user?.firm_name || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 py-4">
                  <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    <Shield className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.role') || 'Rol'}</p>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{getRoleLabel(user?.role)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 py-4">
                  <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    <Calendar className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.memberSince') || 'Üyelik Tarihi'}</p>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 py-4">
                  <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    <Clock className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('profile.lastLogin') || 'Son Giriş'}</p>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{formatDate(user?.last_login_at) || '-'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                {t('profile.accountStatus') || 'Hesap Durumu'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Badge className={`${user?.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} px-3 py-1`}>
                  {user?.status === 'ACTIVE' ? (t('common.active') || 'Aktif') : (t('common.inactive') || 'Pasif')}
                </Badge>
                <span className="text-slate-500 dark:text-slate-400 text-sm">
                  {user?.status === 'ACTIVE' 
                    ? (t('profile.accountActive') || 'Hesabınız aktif durumda')
                    : (t('profile.accountInactive') || 'Hesabınız pasif durumda')}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Language Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-5 w-5 text-purple-600" />
                {t('profile.languageSettings') || 'Dil Ayarları'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Label className="text-slate-600 dark:text-slate-300 min-w-[80px]">
                  {t('auth.language') || 'Dil'}
                </Label>
                <Select value={language} onValueChange={handleLanguageChange} disabled={savingLanguage}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tr">🇹🇷 Türkçe</SelectItem>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                    <SelectItem value="nl">🇳🇱 Nederlands</SelectItem>
                    <SelectItem value="fr">🇫🇷 Français</SelectItem>
                  </SelectContent>
                </Select>
                {savingLanguage && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
            </CardContent>
          </Card>

          {/* Session Lock Timeout Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-600" />
                {t('lockScreen.idleTimeout') || 'Oturum Kilitleme Süresi'}
              </CardTitle>
              <CardDescription>
                {t('lockScreen.idleTimeoutDesc') || 'Hareketsizlik sonrası ekran otomatik kilitlenir'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Slider
                  value={[idleTimeout]}
                  onValueChange={([val]) => setIdleTimeout(val)}
                  onValueCommit={([val]) => handleIdleTimeoutSave(val)}
                  min={1}
                  max={120}
                  step={1}
                  className="flex-1"
                  data-testid="idle-timeout-slider"
                />
                <div className="flex items-center gap-1.5 min-w-[80px] justify-end">
                  {idleTimeoutSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                  <span className="text-lg font-semibold text-slate-700 dark:text-slate-200" data-testid="idle-timeout-value">
                    {idleTimeout} {t('common.minutes') || 'dk'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock className="h-5 w-5 text-amber-600" />
                  {t('profile.changePassword') || 'Şifre Değiştir'}
                </CardTitle>
                {!showPasswordForm && (
                  <Button variant="outline" size="sm" onClick={() => setShowPasswordForm(true)}>
                    {t('profile.changePassword') || 'Şifre Değiştir'}
                  </Button>
                )}
              </div>
            </CardHeader>
            {showPasswordForm && (
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label>{t('profile.currentPassword') || 'Mevcut Şifre'}</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('profile.newPassword') || 'Yeni Şifre'}</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        required
                        minLength={6}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('profile.confirmNewPassword') || 'Yeni Şifre (Tekrar)'}</Label>
                    <Input
                      type="password"
                      value={passwordForm.new_password_confirm}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password_confirm: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button type="submit" disabled={passwordLoading}>
                      {passwordLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('common.loading') || 'Yükleniyor...'}
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {t('common.save') || 'Kaydet'}
                        </>
                      )}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordForm({ current_password: '', new_password: '', new_password_confirm: '' });
                      }}
                    >
                      {t('common.cancel') || 'İptal'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <SecuritySettings />
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <StickyNote className="h-5 w-5 text-amber-500" />
                  Notlarım
                  <Badge variant="secondary" className="text-xs">{myNotes.length}</Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                    <Input
                      placeholder="Not ara..."
                      value={notesSearch}
                      onChange={e => setNotesSearch(e.target.value)}
                      className="pl-8 h-8 text-sm w-48"
                      data-testid="notes-search"
                    />
                  </div>
                  <Input
                    type="date"
                    value={notesDateFilter}
                    onChange={e => setNotesDateFilter(e.target.value)}
                    className="h-8 text-sm w-40"
                    data-testid="notes-date-filter"
                  />
                  {notesDateFilter && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setNotesDateFilter('')}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant={notesShowArchived ? 'default' : 'outline'}
                    size="sm"
                    className="h-8 text-xs gap-1"
                    onClick={() => { setNotesShowArchived(!notesShowArchived); }}
                    data-testid="notes-toggle-archived"
                  >
                    <Archive className="h-3 w-3" />
                    Arşiv
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {notesLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : (() => {
                const filtered = myNotes.filter(n => {
                  if (notesSearch && !turkishIncludes(n.content || '', notesSearch)) return false;
                  if (notesDateFilter) {
                    const noteDate = n.created_at?.slice(0, 10);
                    if (noteDate !== notesDateFilter) return false;
                  }
                  return true;
                });

                if (filtered.length === 0) return (
                  <div className="text-center py-12 text-slate-400">
                    <StickyNote className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{notesSearch || notesDateFilter ? 'Eşleşen not bulunamadı' : 'Henüz not yok'}</p>
                    <p className="text-xs mt-1 text-slate-300">Sayfanın sağ altındaki not butonunu kullanarak hızlıca not ekleyebilirsiniz</p>
                  </div>
                );

                // Group by date
                const grouped = {};
                filtered.forEach(n => {
                  const dateKey = new Date(n.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
                  if (!grouped[dateKey]) grouped[dateKey] = [];
                  grouped[dateKey].push(n);
                });

                const noteColors = {
                  yellow: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
                  blue: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-800' },
                  green: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800' },
                  pink: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800' },
                  purple: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-800' },
                };

                return (
                  <div className="space-y-6">
                    {Object.entries(grouped).map(([date, notes]) => (
                      <div key={date}>
                        <div className="flex items-center gap-2 mb-3">
                          <Calendar className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{date}</span>
                          <span className="text-[10px] text-slate-400">({notes.length} not)</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {notes.map(note => {
                            const c = noteColors[note.color] || noteColors.yellow;
                            return (
                              <div key={note.id} className={`${c.bg} ${c.border} border rounded-xl p-3 group transition-shadow hover:shadow-md ${note.archived ? 'opacity-50' : ''}`} data-testid={`profile-note-${note.id}`}>
                                <p className={`text-sm ${c.text} whitespace-pre-wrap ${!note.content ? 'italic opacity-40' : ''} ${note.archived ? 'line-through' : ''}`}>
                                  {note.content || '(boş not)'}
                                </p>
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-black/5">
                                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                    <Clock className="h-2.5 w-2.5" />
                                    {new Date(note.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                    {note.updated_at !== note.created_at && (
                                      <span className="ml-1 opacity-60">(düzenlendi {new Date(note.updated_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })})</span>
                                    )}
                                  </span>
                                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {note.archived ? (
                                      <button onClick={() => handleNoteUnarchive(note.id)} className="p-1 rounded hover:bg-white/60" title="Geri Al">
                                        <ArchiveRestore className="h-3 w-3 text-emerald-500" />
                                      </button>
                                    ) : null}
                                    <button onClick={() => handleNoteDelete(note.id)} className="p-1 rounded hover:bg-white/60" title="Kalıcı Sil">
                                      <Trash2 className="h-3 w-3 text-red-400" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('employeeProfile.uploadDocument')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('employeeProfile.documentType')} *</Label>
              <Select value={docForm.document_type} onValueChange={(v) => setDocForm({ ...docForm, document_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOCUMENT_TYPE_KEYS).map(([key, i18nKey]) => (
                    <SelectItem key={key} value={key}>{t(`employeeProfile.${i18nKey}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('employeeProfile.selectFile')} *</Label>
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileCheck className="h-5 w-5 text-green-600" />
                    <span className="text-sm">{selectedFile.name}</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('employeeProfile.dragDrop')}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('employeeProfile.fileTypes')}</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx"
                onChange={handleFileSelect}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('employeeProfile.documentNumber')}</Label>
                <Input
                  value={docForm.document_number}
                  onChange={(e) => setDocForm({ ...docForm, document_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('employeeProfile.expiryDate')}</Label>
                <Input
                  type="date"
                  value={docForm.expiry_date}
                  onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('employeeProfile.notes')}</Label>
              <Input
                value={docForm.notes}
                onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>{t('employeeProfile.cancelBtn')}</Button>
            <Button onClick={handleUploadDocument} disabled={uploadingDoc}>
              {uploadingDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {t('employeeProfile.uploadBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation */}
      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('employeeProfile.deleteRecord')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('employeeProfile.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('employeeProfile.cancelBtn')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteDocument} className="bg-red-600 hover:bg-red-700">
              {t('employeeProfile.deleteBtn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile Photo Cropper — self-upload is Super Admin only */}
      {user?.role === 'SUPER_ADMIN' && (
        <ImageCropper
          open={photoCropperOpen}
          onClose={() => setPhotoCropperOpen(false)}
          onComplete={handleUploadProfilePhoto}
          aspectRatio={1}
          cropShape="round"
          title={t('employeeProfile.uploadPhoto') || 'Profil Fotoğrafı Yükle'}
        />
      )}

      {/* Document Image Editor for ID/License */}
      <DocumentImageEditor
        open={docEditorOpen}
        onClose={() => { setDocEditorOpen(false); setDocEditorType(null); }}
        onComplete={handleDocumentEditorComplete}
        documentType={docEditorType}
        title={docEditorType === 'identity_card' 
          ? (t('employeeProfile.identityCard') || 'Kimlik Kartı') + ' Yükle'
          : docEditorType === 'driver_license'
          ? (t('employeeProfile.driverLicense') || 'Ehliyet') + ' Yükle'
          : (t('employeeProfile.passport') || 'Pasaport') + ' Yükle'
        }
      />

      {/* OCR Result Modal - Editable */}
      <Dialog open={ocrModalOpen} onOpenChange={setOcrModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-500" />
              {t('employeeProfile.ocrResults') || 'OCR Tarama Sonuçları'}
            </DialogTitle>
            <DialogDescription>
              {t('employeeProfile.ocrResultsEditableDesc') || 'OCR taramasından bulunan tarihler aşağıda gösterilmektedir. Yanlışsa düzeltebilir veya manuel girebilirsiniz.'}
            </DialogDescription>
          </DialogHeader>
          
          {ocrModalData && (
            <div className="space-y-4 py-4">
              {/* Document Type */}
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <FileText className="h-4 w-4" />
                <span>Belge: </span>
                <Badge variant="outline">
                  {ocrModalData.docType === 'identity_card' ? 'Kimlik Kartı' : 
                   ocrModalData.docType === 'driver_license' ? 'Ehliyet' : 'Pasaport'}
                </Badge>
              </div>
              
              {/* Editable Date Fields */}
              <div className="space-y-4 bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                {/* Expiry Date - Editable */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    📅 {t('employeeProfile.expiryDate') || 'Son Kullanma Tarihi'}
                    {ocrModalData.expiryDate && (
                      <Badge variant="secondary" className="text-xs">OCR</Badge>
                    )}
                  </Label>
                  <Input
                    type="date"
                    value={ocrModalData.expiryDate || ''}
                    onChange={(e) => setOcrModalData({ ...ocrModalData, expiryDate: e.target.value })}
                    className="bg-white dark:bg-slate-700"
                  />
                  {!ocrModalData.expiryDate && (
                    <p className="text-xs text-amber-600">⚠️ OCR son kullanma tarihi bulamadı. Manuel girin.</p>
                  )}
                </div>
                
                {/* Birth Date - Editable (not for Bestuurdersattest) */}
                {ocrModalData.docType !== 'bestuurdersattest' && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      🎂 {t('employeeProfile.birthDate') || 'Doğum Tarihi'}
                      {ocrModalData.birthDate && (
                        <Badge variant="secondary" className="text-xs">OCR</Badge>
                      )}
                    </Label>
                    <Input
                      type="date"
                      value={ocrModalData.birthDate || ''}
                      onChange={(e) => setOcrModalData({ ...ocrModalData, birthDate: e.target.value })}
                      className="bg-white dark:bg-slate-700"
                    />
                  </div>
                )}
                
                {/* Rijksregisternummer - Editable */}
                {ocrModalData.docType === 'identity_card' && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium" data-testid="ocr-rijksregisternr-label">
                      🆔 {t('employeeProfile.nationalId') || 'Rijksregisternummer'}
                      {ocrModalData.rijksregisternr && (
                        <Badge variant="secondary" className="text-xs">OCR</Badge>
                      )}
                    </Label>
                    <Input
                      type="text"
                      data-testid="ocr-rijksregisternr-input"
                      placeholder="YY.MM.DD-XXX.CC"
                      value={ocrModalData.rijksregisternr || ''}
                      onChange={(e) => {
                        const raw = stripRRN(e.target.value);
                        if (raw.length <= 11) {
                          setOcrModalData({ ...ocrModalData, rijksregisternr: raw.length === 11 ? formatRRN(raw) : e.target.value });
                        }
                      }}
                      className={(() => {
                        const v = validateRRN(ocrModalData.rijksregisternr, ocrModalData.birthDate);
                        if (!ocrModalData.rijksregisternr || stripRRN(ocrModalData.rijksregisternr).length < 11) return 'bg-white dark:bg-slate-700 font-mono';
                        if (!v.valid) return 'bg-white dark:bg-slate-700 font-mono border-red-500';
                        if (v.warning) return 'bg-white dark:bg-slate-700 font-mono border-amber-500';
                        return 'bg-white dark:bg-slate-700 font-mono border-green-500';
                      })()}
                    />
                    {(() => {
                      const digits = stripRRN(ocrModalData.rijksregisternr);
                      if (!digits || digits.length < 11) {
                        return !ocrModalData.rijksregisternr ? <p className="text-xs text-amber-600">OCR rijksregisternummer bulamadı. Manuel girin.</p> : null;
                      }
                      const result = validateRRN(ocrModalData.rijksregisternr, ocrModalData.birthDate);
                      const err = getRRNErrorMessage(result);
                      const warn = getRRNWarningMessage(result);
                      if (err) return <p className="text-xs text-red-600">{err}</p>;
                      if (warn) return <p className="text-xs text-amber-600">{warn}</p>;
                      return <p className="text-xs text-green-600">{result.gender === 'M' ? 'Erkek' : 'Kadın'} - {result.birthYear} doğumlu</p>;
                    })()}
                  </div>
                )}
              </div>
              
              {/* Other Found Dates - Clickable to Select */}
              {ocrModalData.allDates && ocrModalData.allDates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    {t('employeeProfile.ocrAllDatesHint') || 'Bulunan diğer tarihler (tıklayarak seçin):'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ocrModalData.allDates.map((date, idx) => (
                      <Button
                        key={idx}
                        variant={date === ocrModalData.expiryDate ? "default" : "outline"}
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setOcrModalData({ ...ocrModalData, expiryDate: date })}
                        title="Son kullanma tarihi olarak seç"
                      >
                        {new Date(date).toLocaleDateString('tr-TR')}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* OCR Raw Text Preview */}
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                  OCR Ham Metin (Gelişmiş)
                </summary>
                <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-900 rounded text-xs overflow-auto max-h-32 whitespace-pre-wrap">
                  {ocrModalData.text}
                </pre>
              </details>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setOcrModalOpen(false); setOcrModalData(null); }}>
              {t('common.cancel') || 'İptal'}
            </Button>
            <Button 
              onClick={handleOcrConfirm} 
              className="bg-green-600 hover:bg-green-700"
              data-testid="ocr-confirm-btn"
              disabled={!ocrModalData?.expiryDate && !ocrModalData?.birthDate && !ocrModalData?.rijksregisternr}
            >
              <Check className="h-4 w-4 mr-2" />
              {t('employeeProfile.applyOcrData') || 'Verileri Uygula'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal (Images) */}
      <DocumentPreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
        title={previewDoc?.file_name || previewDoc?.original_name}
      />
    </div>
  );
}
